/* eslint-disable no-unreachable */

// ----------------------------------------------------------------------
import { NavLink as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useContext, useEffect, useRef, useState } from 'react';

// @mui
import {
  TextField,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Radio,
  FormControlLabel,
} from '@mui/material';
import FmdGoodIcon from '@mui/icons-material/FmdGood';

// third-party
import axios from 'axios';
import toast from 'react-hot-toast';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';

// project-imports
import Logo from '../../components/logo/Logo';
import styles from './Auth.module.css';
import { BaseUrlContext } from '../../context/BaseUrlContext';
import bus from '../../bus';

// ----------------------------------------------------------------------

export default function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [category, setCategory] = useState('shop');
  const [StoreID, setStoreID] = useState(false);
  const [StoreIDValue, setStoreIDValue] = useState('');
  const [email, setEmail] = useState('');
  const [storeName, setStoreName] = useState('');
  const [locationCoords, setLocationCoords] = useState({ latitude: '', longitude: '' });
  const [storeAddress, setStoreAddress] = useState('');
  const [storePhone, setStorePhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm_password, setConfirmPassword] = useState('');
  const [agree, setAgree] = useState(false);
  const [doesNotMatch, setDoesNotMatch] = useState(false);
  const [signUpError, setSignUpError] = useState(null);
  const [response, setResponse] = useState(null);
  const [isPending, setIsPending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [useCurrentLocation, setUseCurrentLocation] = useState(false);
  const [addressResults, setAddressResults] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const baseUrl = useContext(BaseUrlContext);

  const [pageContent, setPageContent] = useState(
    'Track and monitor <br /> your customers on <br /> a single dashboard'
  );
  const [pageDescription, setDescription] = useState(
    '  Make informed sales decisions by monitoring consumer <br /> behaviours, brand acceptance and market performance.'
  );
  const [bg, setBg] = useState('/assets/happyMan.jpg');
  const prevCategoryRef = useRef('');

  const [onBoardedBy, setOnBoardedBy] = useState('self');
  const [onBoardedByName, setOnBoardedByName] = useState('');

  useEffect(() => {
    if (category !== '' && category !== prevCategoryRef.current) {
      if (category === 'brand') {
        setPageContent('Track and monitor <br /> your customers on <br /> a single dashboard');
        setDescription(
          'Make informed sales decisions by monitoring consumer <br /> behaviours, brand acceptance and market performance.'
        );
        setBg('/assets/happyMan.jpg');
      } else {
        setPageContent(' Buy, sell and monitor <br />  your customers on a <br /> single platform');
        setDescription(
          ' Make purchases and keep track of all your business transactions in one <br /> convenient place.'
        );
        setBg('/assets/retailerBg.svg');
      }
      prevCategoryRef.current = category;
    }
  }, [category]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          if (latitude !== null && longitude !== null) {
            setSelectedLocation({ latitude, longitude });
            // This is a free key, consider moving to backend and securing it.
            fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?address=${latitude},+${longitude}&key=AIzaSyCvH_nDKUVAcmjtKEbv-_YDY9rVbGqg4VQ`
            )
              .then((response) => response.json())
              .then((data) => {
                if (data.results && data.results.length > 0) {
                  const uniqueAddresses = [...new Set(data.results.map(r => r.formatted_address))];
                  setAddressResults(uniqueAddresses);
                } else {
                  console.error('No address found for the given coordinates.');
                }
              })
              .catch((error) => {
                console.error('Error fetching address:', error);
              });
          } else {
            console.error('Latitude or longitude is null.');
          }
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    } else {
      console.error('Geolocation is not supported by this browser.');
    }
  }, []);
  
  const handleUseLiveLocation = () => {
      setUseCurrentLocation(prev => !prev);
      if (!useCurrentLocation && addressResults.length > 0) {
        setStoreAddress(addressResults[0]);
      }
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(location?.search);
    const storeID = searchParams.get('StoreID');
    if (storeID) {
      setStoreID(true);
      setStoreIDValue(storeID);
    }
  }, [location]);

  useEffect(() => {
    const onRegisterData = localStorage.getItem('onRegister');
    if(onRegisterData){
        const savedData = JSON.parse(onRegisterData);
        setCategory(savedData.category);
        setEmail(savedData.email);
        setPassword(savedData.password);
        setConfirmPassword(savedData.confirm_password);
    }
    const storeIDData = localStorage.getItem('storeID');
    if(storeIDData){
        setStoreIDValue(JSON.parse(storeIDData));
        setStoreID(true);
    }
  }, []);

  async function HandleFormSubmit(e) {
    e.preventDefault();

    if (confirm_password !== password) {
      setDoesNotMatch(true);
      return;
    }

    if (password.length < 8) {
      setSignUpError('Password must be at least 8 characters long');
      return;
    }

    setSignUpError(null);
    setDoesNotMatch(false);

    const payload = {
      merchant_type: StoreID ? 'shop' : category,
      email,
      password,
      confirm_password,
      latitude: locationCoords.latitude,
      longitude: locationCoords.longitude,
      referrer_name: onBoardedByName,
      onboarded_by: onBoardedBy,
      onboarder_id: onBoardedBy === 'agent' ? onBoardedByName : '',
    };

    if (payload.merchant_type !== 'brand') {
      payload.store_address = storeAddress;
      payload.store_name = storeName;
      payload.store_phone_number = storePhone;
    }
    
    if(StoreID) {
        payload.shopID = StoreIDValue;
    }

    try {
      setIsPending(true);
      const result = await axios.post(`${baseUrl}/merchant/create`, payload);
      
      localStorage.setItem('e', email);
      localStorage.setItem('mt', payload.merchant_type);
      setResponse(result.data.message);
      bus.emit('email', email);
      
      setPassword('');
      setConfirmPassword('');
      setEmail('');
      setCategory('manufacturer'); // intended?

      toast.success('Registration successful .');

      localStorage.removeItem('onRegister');
      localStorage.removeItem('storeID');
      
      setTimeout(() => {
        navigate('/verify_email');
      }, 2000);

    } catch (error) {
      setSignUpError(error.response?.data?.message || "An error occurred");
      toast.error("That didn't work please try again later .");
    } finally {
      setIsPending(false);
    }
  }

  const handleStoreAddressSelect = async (value) => {
    const results = await geocodeByAddress(value);
    const { lat, lng } = await getLatLng(results[0]);
    setLocationCoords({ latitude: lat, longitude: lng });
    setStoreAddress(value);
  };

  const handlePrivacyPolicyClick = () => {
    const onRegister = { email, password, confirm_password, category };
    localStorage.setItem('onRegister', JSON.stringify(onRegister));
    if(StoreID) {
        localStorage.setItem('storeID', JSON.stringify(StoreIDValue));
    }
    navigate('/privacy-policy');
  }

  return (
    <div className={`row ${styles.sav}`}>
      <div className="col-md-6 d-none d-md-block">
        <div
          className={styles.bg}
          style={{
            backgroundImage: `linear-gradient(209.36deg, rgba(175, 53, 133, 0.75) 0%, rgba(19, 1, 34, 0.75) 100%), url(${bg})`,
            backgroundPosition: 'top center',
          }}
        >
          <img className={styles.star} src="/assets/Stars.svg" alt="stars" />
          <div className={styles.content}>
            <h5 className={styles.header_text} dangerouslySetInnerHTML={{ __html: pageContent }} />
            <p dangerouslySetInnerHTML={{ __html: pageDescription }} />
            <div className={styles.icon_n_text}>
              <img className={styles.icons} src="/assets/Avatars.svg" alt="avatars" />
              <span style={{ color: '#EAECF0' }}>Join 40,000+ users</span>
            </div>
          </div>
        </div>
      </div>
      <div className="col-md-6 ">
        <Logo />
        <div className={`${styles.sign_up} container`}>
          <form onSubmit={HandleFormSubmit}>
            <h4 className={`mb-4 ${styles.signInHeader}`} style={{ fontSize: '36px', fontWeight: '500', color: '#111827', textAlign: 'left' }}>
              Sign up
            </h4>
            
            {signUpError && <Alert severity="error" style={{ border: '1px solid red' }}>{signUpError}</Alert>}
            {response && <Alert severity="success" style={{ border: '1px dotted gray' }}>{response}</Alert>}

            {!StoreID && (
              <div className="my-4 inputs_n_label">
                <label htmlFor="business-category">Business Category</label>
                <Select
                  id="business-category"
                  fullWidth
                  size="small"
                  value={category}
                  placeholder="Select business category"
                  required
                  onChange={(e) => setCategory(e.target.value)}
                  sx={{ color: '#4b5563' }}
                  MenuProps={{ PaperProps: { sx: { '& .MuiMenuItem-root': { color: '#4b5563' } } } }}
                >
                  <MenuItem value="brand">Manufacturer</MenuItem>
                  <MenuItem value="kiosk">Kiosk</MenuItem>
                  <MenuItem value="ministore">Ministore</MenuItem>
                  <MenuItem value="supermarket">Supermarket</MenuItem>
                </Select>
              </div>
            )}

            <div className="my-4 inputs_n_label">
                <label htmlFor="onboarded-by">Onboarded by*</label>
                <Select
                  id="onboarded-by"
                  fullWidth
                  size="small"
                  value={onBoardedBy}
                  required
                  onChange={(e) => setOnBoardedBy(e.target.value)}
                  sx={{ color: '#4b5563' }}
                  MenuProps={{ PaperProps: { sx: { '& .MuiMenuItem-root': { color: '#4b5563' } } } }}
                >
                  <MenuItem value="self">Myself</MenuItem>
                  <MenuItem value="agent">Agent</MenuItem>
                </Select>
            </div>

            {onBoardedBy === 'agent' && (
                <div className="my-4 inputs_n_label">
                    <label htmlFor="onboarder-name">Name</label>
                    <TextField
                        type="text"
                        value={onBoardedByName}
                        onChange={(e) => setOnBoardedByName(e.target.value)}
                        id="onboarder-name"
                        placeholder="Enter agent's name"
                        size="small"
                        fullWidth
                        required
                    />
                </div>
            )}

            <div className="my-4 inputs_n_label">
              <label htmlFor="email">Email</label>
              <TextField
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                id="email"
                placeholder="Joe@shoprite.com"
                size="small"
                fullWidth
              />
            </div>

            {category !== 'brand' && (
              <>
                <div className="my-4 inputs_n_label">
                  <label htmlFor="store_name">Store Name</label>
                  <TextField
                    type="text"
                    value={storeName}
                    onChange={(e) => setStoreName(e.target.value)}
                    id="store_name"
                    placeholder="shoprite"
                    size="small"
                    fullWidth
                  />
                </div>

                <PlacesAutocomplete
                  value={storeAddress}
                  onChange={setStoreAddress}
                  onSelect={handleStoreAddressSelect}
                >
                  {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                    <div>
                        <div className="my-4 inputs_n_label">
                            <label htmlFor="loc">Store Address</label>
                            <div style={{position: 'relative', width: '210px'}}>
                                {!useCurrentLocation && (
                                    <p className="mt-2" onClick={handleUseLiveLocation} style={{cursor: 'pointer', fontSize: '15px'}}>
                                        <FmdGoodIcon /> Use My Current Location
                                    </p>
                                )}
                                {useCurrentLocation && (
                                     <p className="mt-2" onClick={handleUseLiveLocation} style={{cursor: 'pointer', fontSize: '15px'}}>
                                        <FmdGoodIcon /> Manual Location (Edit)
                                     </p>
                                )}
                            </div>

                            {useCurrentLocation && (
                                <div className={`${styles.syl} d-flex mt-0`}>
                                    <FormControlLabel value={storeAddress} checked control={<Radio />} label="" className="m-0" />
                                    <Select
                                        fullWidth
                                        size="small"
                                        value={storeAddress}
                                        required
                                        onChange={(e) => setStoreAddress(e.target.value)}
                                    >
                                        {addressResults.map((address, i) => <MenuItem key={i} value={address}>{address}</MenuItem>)}
                                    </Select>
                                </div>
                            )}

                            {!useCurrentLocation && (
                                <TextField
                                    id="loc"
                                    placeholder="Input your location"
                                    size="small"
                                    fullWidth
                                    {...getInputProps({
                                    placeholder: 'Search Places ...',
                                    className: 'location-search-input',
                                    })}
                                />
                            )}
                            <div className="autocomplete-dropdown-container">
                                {loading && <div>Loading...</div>}
                                {suggestions.map((suggestion, i) => {
                                const className = suggestion.active
                                    ? 'suggestion-item--active'
                                    : 'suggestion-item';
                                const style = suggestion.active
                                    ? { backgroundColor: '#fafafa', cursor: 'pointer' }
                                    : { backgroundColor: '#ffffff', cursor: 'pointer' };
                                return (
                                    <div key={i} {...getSuggestionItemProps(suggestion, { className, style })}>
                                    <span>{suggestion.description}</span>
                                    </div>
                                );
                                })}
                            </div>
                        </div>
                    </div>
                  )}
                </PlacesAutocomplete>

                <div className="my-4 inputs_n_label">
                  <label htmlFor="store_phone">Store Phone</label>
                  <TextField
                    type="text"
                    value={storePhone}
                    onChange={(e) => setStorePhone(e.target.value)}
                    id="store_phone"
                    placeholder="+234 xxx xxx xxxx."
                    size="small"
                    fullWidth
                  />
                </div>
              </>
            )}

            <div className="my-4 inputs_n_label">
              <label htmlFor="password">Password</label>
              <TextField
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                id="password"
                placeholder="Create a password"
                size="small"
                fullWidth
              />
              <div className="icon" onClick={() => setShowPassword(!showPassword)}>
                {!showPassword && <img src="/assets/icons/eye.svg" alt="show password"/>}
                {showPassword && <img src="/assets/icons/eye-off.svg" alt="hide password" />}
              </div>
              <small className={styles.lessBoldText}>Enter at least 8 characters</small>
            </div>

            <div className="my-4 inputs_n_label">
              <label htmlFor="c_pass">Confirm password</label>
              <TextField
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirm_password}
                onInput={(e) => setConfirmPassword(e.target.value)}
                id="c_pass"
                placeholder="Confirm password"
                size="small"
                fullWidth
              />
              <div className="icon mt-2" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                 {!showConfirmPassword && <img src="/assets/icons/eye.svg" alt="show password" />}
                 {showConfirmPassword && <img src="/assets/icons/eye-off.svg" alt="hide password" />}
              </div>
              {doesNotMatch && <small className="text-danger">Password confirmation does not match</small>}
            </div>
            
            {StoreID && (
                <div className="my-4 inputs_n_label">
                    <label htmlFor="store-id">Store ID</label>
                    <TextField
                        disabled
                        value={StoreIDValue}
                        id="store-id"
                        size="small"
                        fullWidth
                    />
                </div>
            )}

            <div className="form-check-inline" style={{ color: '#4b5563' }}>
              <label htmlFor="agree" className="form-check-label" style={{ color: '#4b5563', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>
                <input
                  id="agree"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  type="checkbox"
                  className="form-check-input"
                />
                I agree with the
                <span onClick={handlePrivacyPolicyClick} className="ml-1" style={{ color: '#53138D', cursor: 'pointer' }}>
                  terms of the service and conditions
                </span>
              </label>
            </div>
            
            <div className={styles.buttons}>
                <button type="submit" disabled={!agree || isPending} className={`btn btn-block ${styles.btn_custom_color}`}>
                    <span className={styles.span}>
                        {isPending ? 'Signing you in...' : 'Get started'}
                    </span>
                    {isPending && <CircularProgress size="1rem" style={{color: 'white'}}/>}
                </button>
                <p className="text-center" style={{ color: '#4b5563' }}>
                    Already have an account?{' '}
                    <RouterLink to="/login" style={{ color: '#53138D' }}>
                        Sign in
                    </RouterLink>
                </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
